import { Timeline, Typography } from 'antd';
import type { DetalheDaPessoa } from '@titularis/api-client';
import { formatarData } from '../../dominio';

/**
 * A jornada da conta, do cadastro à compra.
 *
 * **Os marcos que ainda não aconteceram continuam na lista**, em cinza, e é o
 * ponto do desenho: mostrar só o que já foi feito responde "o que ela fez", e a
 * pergunta desta tela é **onde ela parou** - que só se enxerga vendo o degrau
 * vazio logo abaixo do último cheio.
 *
 * **"Confirmou o e-mail" não tem data, e não é esquecimento.** O BetterAuth
 * guarda um sim ou não na coluna `email_verified`, e a linha da tabela de
 * verificação é apagada no uso: não existe no banco a data em que a confirmação
 * aconteceu. Carimbar aqui o `updated_at` da conta seria inventar um fato - e um
 * fato inventado numa linha do tempo é pior que uma lacuna, porque parece
 * medido.
 */
export function JornadaDaPessoa({ jornada }: { jornada: DetalheDaPessoa['jornada'] }) {
  const marcos: { titulo: string; em: string | null; feito: boolean; nota?: string }[] = [
    {
      titulo: 'Entrou na lista de espera',
      em: jornada.entrouNaLista,
      feito: Boolean(jornada.entrouNaLista),
      nota: 'Pediu para ser avisada antes de ter conta.',
    },
    { titulo: 'Criou a conta', em: jornada.criouConta, feito: true },
    {
      titulo: 'Confirmou o e-mail',
      em: null,
      feito: jornada.confirmouEmail,
      nota: jornada.confirmouEmail
        ? 'A data não existe no banco: o que se guarda é o sim ou não.'
        : 'Enquanto não confirmar, a conta não responde questão nenhuma.',
    },
    {
      titulo: 'Concluiu as boas-vindas',
      em: jornada.concluiuOnboarding,
      feito: Boolean(jornada.concluiuOnboarding),
    },
    {
      titulo: 'Estudou pela primeira vez',
      em: jornada.primeiroEstudo,
      feito: Boolean(jornada.primeiroEstudo),
      nota: 'Respondeu, abriu uma resolução ou leu um artigo de lei.',
    },
    {
      titulo: 'Abriu um checkout',
      em: jornada.primeiroPedido,
      feito: Boolean(jornada.primeiroPedido),
    },
    { titulo: 'Pagou', em: jornada.primeiraCompra, feito: Boolean(jornada.primeiraCompra) },
  ];

  /*
   * A inscrição em lista só aparece quando existe. Ela não é um degrau de todo
   * mundo - a maior parte das contas nasce direto no cadastro -, e uma linha
   * cinza permanente no topo diria que todo aluno deveria ter passado por ali.
   */
  const visiveis = marcos.filter(
    (marco) => marco.titulo !== 'Entrou na lista de espera' || marco.feito,
  );

  return (
    <Timeline
      items={visiveis.map((marco) => ({
        color: marco.feito ? 'green' : 'gray',
        title: (
          <Typography.Text type={marco.feito ? undefined : 'secondary'} strong={marco.feito}>
            {marco.titulo}
          </Typography.Text>
        ),
        content: (
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {marco.feito && marco.em ? formatarData(marco.em) : null}
            {marco.feito && marco.em && marco.nota ? ' · ' : null}
            {marco.nota ?? (marco.feito ? null : 'ainda não')}
          </Typography.Text>
        ),
      }))}
    />
  );
}
